import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "@/hooks/use-toast";
import { logEdgeFunctionError } from "@/utils/sentry";

interface TrialSkipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TrialSkipDialog = ({ open, onOpenChange }: TrialSkipDialogProps) => {
  const { refresh } = useSubscription();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("end-trial-now");

      if (error) {
        const fnError = (data as { error?: string } | null)?.error;
        const message =
          fnError || "Nie udało się aktywować subskrypcji. Spróbuj ponownie.";
        toast({
          title: "Błąd",
          description: message,
          variant: "destructive",
        });
        logEdgeFunctionError("end-trial-now", error);
        return;
      }

      toast({
        title: "Subskrypcja aktywowana",
        description: "Pełen dostęp do planu jest już Twój.",
      });
      await refresh();
      onOpenChange(false);
      // useTierLimits derives tier from useSubscription.subscriptionTrialEnd,
      // which can be read from DB before the Stripe webhook clears it — so
      // pages like Competitors render with stale trial limits until reload.
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      toast({
        title: "Błąd",
        description: "Nie udało się aktywować subskrypcji. Spróbuj ponownie.",
        variant: "destructive",
      });
      logEdgeFunctionError("end-trial-now", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500" />
            Aktywuj subskrypcję teraz
          </DialogTitle>
          <DialogDescription className="pt-2">
            Pominiesz pozostałą część okresu próbnego i od razu otrzymasz pełen dostęp do swojego planu.
          </DialogDescription>
        </DialogHeader>

        <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
          <li>Okres próbny zostanie zakończony natychmiast.</li>
          <li>
            Z zapisanej metody płatności zostanie pobrana pełna kwota Twojego planu.
          </li>
          <li>Wszystkie limity konta zostaną odblokowane od razu.</li>
        </ul>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Anuluj
          </Button>
          <Button onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Aktywuję...
              </>
            ) : (
              "Tak, aktywuj subskrypcję"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TrialSkipDialog;
